import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Form, Spinner, Table } from 'react-bootstrap';
import EmptyState from '../../components/EmptyState';
import StatusBadge from '../../components/StatusBadge';
import accountApi from '../../services/accountApi';

function unwrapList(data) {
  if (Array.isArray(data)) return { results: data, count: data.length };
  return data || { results: [], count: 0 };
}

function PlatformUsersPage() {
  const [users, setUsers] = useState([]);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (activeFilter) params.is_active = activeFilter;
      const data = unwrapList(await accountApi.listPlatformUsers(params));
      setUsers(data.results || []);
      setCount(data.count ?? (data.results || []).length);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <h1 className="product-page-title">Users</h1>
      <p className="product-page-subtitle">Registered Bakeonator accounts.</p>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="product-card mb-3">
        <Form
          className="row g-2 align-items-end"
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
        >
          <div className="col-md-6">
            <Form.Label>Search</Form.Label>
            <Form.Control
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Exact email or name fragment"
            />
          </div>
          <div className="col-md-3">
            <Form.Label>Active</Form.Label>
            <Form.Select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Form.Select>
          </div>
          <div className="col-md-3">
            <button type="submit" className="btn btn-outline-primary w-100">
              Apply
            </button>
          </div>
        </Form>
      </div>

      {loading && (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading…
        </div>
      )}

      {!loading && (
        <div className="product-card">
          <div className="small text-muted mb-2">{count} user{count === 1 ? '' : 's'}</div>
          <Table responsive className="product-table" size="sm">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Active</th>
                <th>Memberships</th>
                <th>Platform roles</th>
                <th>Last login</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState title="No users matched" description="Try a different search." />
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <Link to={`/platform/users/${u.id}`}>{u.email}</Link>
                    </td>
                    <td>{u.display_name}</td>
                    <td>
                      <StatusBadge status={u.is_active ? 'active' : 'suspended'} />
                    </td>
                    <td>{u.membership_count ?? (u.memberships || []).length}</td>
                    <td>{(u.platform_roles || []).join(', ') || '—'}</td>
                    <td className="small">
                      {u.last_login ? new Date(u.last_login).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default PlatformUsersPage;
